import React, { useCallback, useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Send, Mail } from "lucide-react";

export default function AdminMessages() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => http.get("/messages").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const open = async (m) => {
    setSelected(m);
    setReply("");
    if (m.status === "unread") {
      await http.patch(`/messages/${m.message_id}/read`);
      load();
    }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await http.post(`/messages/${selected.message_id}/reply`, { reply });
      toast.success("Risposta inviata via email");
      setReply("");
      const { data } = await http.get("/messages");
      setItems(data);
      const fresh = data.find((m) => m.message_id === selected.message_id);
      if (fresh) setSelected(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-10">
        <div className="eyebrow mb-2">Inbox</div>
        <h1 className="font-serif text-4xl mb-8">Messaggi</h1>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="border border-[var(--mel-border)] rounded-md bg-white overflow-hidden max-h-[70vh] overflow-y-auto" data-testid="msg-list">
            {items.length === 0 && <div className="p-6 text-sm text-[var(--mel-muted)]">Nessun messaggio.</div>}
            {items.map((m) => (
              <button
                key={m.message_id}
                onClick={() => open(m)}
                data-testid={`msg-${m.message_id}`}
                className={`w-full text-left p-4 border-b border-[var(--mel-border)] hover:bg-[var(--mel-surface)] transition-colors ${selected?.message_id === m.message_id ? "bg-[var(--mel-surface)]" : ""}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-medium">{m.name}</div>
                  {m.status === "unread" && <span className="w-2 h-2 rounded-full bg-[var(--mel-primary)]"></span>}
                </div>
                <div className="text-xs text-[var(--mel-muted)]">{m.subject || "(senza oggetto)"}</div>
                <div className="text-xs text-[var(--mel-muted)] line-clamp-1 mt-1">{m.body}</div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-2">
            {!selected ? (
              <div className="border border-[var(--mel-border)] rounded-md p-16 text-center text-[var(--mel-muted)] bg-white">
                <Mail size={32} className="mx-auto mb-3" />
                Seleziona un messaggio.
              </div>
            ) : (
              <div className="border border-[var(--mel-border)] rounded-md bg-white p-8" data-testid="msg-detail">
                <div className="mb-6">
                  <div className="font-serif text-2xl">{selected.subject || "(senza oggetto)"}</div>
                  <div className="text-sm text-[var(--mel-muted)] mt-1">Da <strong>{selected.name}</strong> · {selected.email}{selected.phone && ` · ${selected.phone}`}</div>
                </div>
                <div className="p-4 bg-[var(--mel-surface)] rounded-md text-sm whitespace-pre-line">{selected.body}</div>

                {(selected.replies || []).length > 0 && (
                  <div className="mt-6 space-y-3">
                    <div className="eyebrow">Risposte inviate</div>
                    {selected.replies.map((r) => (
                      <div key={r.reply_id} className="p-4 rounded-md border border-[var(--mel-border)] text-sm">
                        <div className="text-xs text-[var(--mel-muted)] mb-2">{r.author} · {new Date(r.created_at).toLocaleString()}</div>
                        <div className="whitespace-pre-line">{r.body}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-8">
                  <div className="eyebrow mb-2">Rispondi</div>
                  <textarea rows="5" value={reply} onChange={(e) => setReply(e.target.value)} className="w-full border border-[var(--mel-border)] rounded-md px-3 py-2 text-sm" placeholder="Scrivi la risposta..." data-testid="reply-body" />
                  <button onClick={send} disabled={busy || !reply.trim()} className="mel-btn-primary mt-3" data-testid="reply-send">
                    <Send size={14} /> {busy ? "Invio…" : "Invia risposta"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
